import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import { Link } from 'react-router-dom';
import { validateSession } from '../utils/functions';
import { vinoDelHome, homeDelModo } from '../utils/appMode';

// Dashboard del operador: conteo A CIEGAS. Cada campo se autosalva como borrador.
// "Confirmar conteo" compara contra el sistema SIN mostrarlo: si coincide cierra, si no
// pide RECONTAR; tras el recuento, si sigue difiriendo va al control.
//
// Matices de esa regla, pedidos por el dueño:
//  - "Según el sistema debería haber X" lo manda el backend SÓLO al admin, y desde el primer
//    conteo (2026-09-23). Antes lo veía cualquiera en el recuento, y recontar era copiarlo.
//  - La discrepancia final abre un campo de MOTIVO, opcional, que viaja al control (2026-09-07).
//  - Cada valor que se deja en un campo viaja con cuánto tiempo estuvo el campo en foco: el
//    backend anota la hora de cada carga para el control (traza silenciosa). Acá no se ve.
const config = process.env;
const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;
const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

const STATUS = { PENDING: 1, COUNTED: 2, DISCREPANCY: 3 };

// Los responsables se recuerdan en el browser de la caja: es casi siempre la misma gente y
// escribirlos de nuevo todos los días es la forma más rápida de que dejen de escribirlos.
const RESPONSIBLES_KEY = 'stockCountResponsibles';

class StockCountPage extends Component {
    constructor() {
        super();
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: Number(localStorage.getItem('role') || 0),
            items: [],
            inputs: {},
            notes: {},        // motivo por producto (texto en pantalla)
            savedNotes: {},   // motivo por producto ya guardado en el backend
            responsibles: localStorage.getItem(RESPONSIBLES_KEY) || '',
            summary: null, // resultado del último "Confirmar conteo"
            loading: true,
            error: null,
        };
        // Último valor mandado al backend por producto. Campo de instancia y no estado: no se
        // pinta, y tiene que quedar marcado ANTES del fetch —"Confirmar" sólo reenvía lo que no
        // salió al salir del campo, y así no duplica en la traza la carga que el blur del último
        // campo está mandando en ese mismo instante—.
        this.lastSent = {};
        this.focusedAt = {};
    }

    async componentDidMount() {
        this._desdeHome = vinoDelHome(window.location.search);
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        await this.loadItems();
    }

    fetchJson = async (path, options) => {
        const res = await fetch(apiBase() + path, { headers: authHeaders(), ...options });
        return res.json();
    }

    loadItems = async () => {
        try {
            const body = await this.fetchJson('stock/dailyItems', { method: 'GET' });
            if (body.error === 0) {
                const items = body.data.items || [];
                const inputs = {};
                const notes = {};
                items.forEach((it) => {
                    if (it.draftQuantity != null) inputs[it.productCode] = String(it.draftQuantity);
                    if (it.countNote != null) notes[it.productCode] = it.countNote;
                });
                // `savedNotes` arranca igual que `notes`: lo que vino del backend ya está
                // guardado, y sin esto la pantalla mostraría "guardado" recién al reescribirlo.
                // Lo mismo `lastSent` con los borradores.
                this.lastSent = { ...inputs };
                this.setState({ items, inputs, notes, savedNotes: { ...notes }, loading: false });
            } else {
                this.setState({ error: 'No se pudo cargar la lista del día', loading: false });
            }
        } catch (e) {
            this.setState({ error: 'Error de red', loading: false });
        }
    }

    onChange = (code, value) => {
        this.setState((s) => ({ inputs: { ...s.inputs, [code]: value } }));
    }

    onChangeNote = (code, value) => {
        this.setState((s) => ({ notes: { ...s.notes, [code]: value } }));
    }

    onChangeResponsibles = (value) => {
        this.setState({ responsibles: value });
    }

    onFocusInput = (code) => {
        this.focusedAt[code] = Date.now();
    }

    // `source`: 'blur' al salir del campo, 'confirm' cuando lo reenvía "Confirmar conteo". El
    // tiempo en foco sólo tiene sentido en el blur.
    saveDraft = async (code, source = 'blur') => {
        const raw = this.state.inputs[code];
        const value = raw == null ? '' : raw;
        if (value === (this.lastSent[code] == null ? '' : this.lastSent[code])) {
            return;
        }
        const focusedAt = source === 'blur' ? this.focusedAt[code] : null;
        const elapsedMs = focusedAt ? Date.now() - focusedAt : null;
        this.lastSent[code] = value;
        try {
            const body = await this.fetchJson('stock/draft', {
                method: 'PATCH',
                body: JSON.stringify({ code: String(code), quantity: value === '' ? null : value, source, elapsedMs }),
            });
            if (body.error !== 0) throw new Error('draft');
        } catch (e) {
            // No quedó guardado: que "Confirmar" lo vuelva a mandar.
            delete this.lastSent[code];
        }
    }

    // Autosave del motivo al salir del campo, igual que el borrador del conteo. Se confirma en
    // pantalla ("guardado") porque acá no hay ningún otro botón que le diga al operador que su
    // texto quedó: sin el aviso, el campo se lee como una nota para uno mismo.
    saveNote = async (code) => {
        const value = this.state.notes[code] || '';
        if (value === (this.state.savedNotes[code] || '')) {
            return;
        }
        try {
            const body = await this.fetchJson('stock/count-note', {
                method: 'PATCH',
                body: JSON.stringify({ code: String(code), note: value }),
            });
            if (body.error === 0) {
                this.setState((s) => ({ savedNotes: { ...s.savedNotes, [code]: value }, error: null }));
            } else {
                this.setState({ error: 'No se pudo guardar el motivo' });
            }
        } catch (e) {
            this.setState({ error: 'No se pudo guardar el motivo' });
        }
    }

    confirmar = async () => {
        const responsibles = this.state.responsibles.trim();
        if (responsibles === '') {
            return;
        }
        try {
            const codes = Object.keys(this.state.inputs)
                .filter((c) => this.state.inputs[c] !== '' && this.state.inputs[c] != null);
            for (const code of codes) {
                await this.saveDraft(code, 'confirm');
            }
            const body = await this.fetchJson('stock/check', {
                method: 'POST',
                body: JSON.stringify({ responsibleNames: responsibles }),
            });
            if (body.error === 0) {
                localStorage.setItem(RESPONSIBLES_KEY, responsibles);
                this.setState({ summary: body.data.summary, error: null });
                await this.loadItems();
            } else {
                this.setState({ error: 'No se pudo confirmar el conteo' });
            }
        } catch (e) {
            this.setState({ error: 'Error de red' });
        }
    }

    renderEstado(it) {
        const status = Number(it.status);
        const needsRecount = status === STATUS.PENDING && Number(it.attemptNo) > 0;
        if (status === STATUS.COUNTED) return <span className="text-success">✓ Contado</span>;
        if (status === STATUS.DISCREPANCY) return <span className="text-danger">Discrepancia — la revisa el control</span>;
        if (needsRecount) return <span className="text-warning">No coincidió — recontá</span>;
        return <span className="text-muted">Pendiente</span>;
    }

    // Campo de motivo de la discrepancia: opcional. El que contó suele saber por qué difiere
    // ("dos estaban en la vidriera") y hoy eso se pierde entre el mostrador y el control.
    renderMotivo(it) {
        const code = it.productCode;
        const value = this.state.notes[code] || '';
        const guardado = value !== '' && value === (this.state.savedNotes[code] || '');
        return (
            <div className="mt-2">
                <input
                    type="text"
                    className="form-control form-control-sm"
                    maxLength={500}
                    placeholder="Motivo (opcional): ¿por qué creés que no coincide?"
                    value={value}
                    onChange={(e) => this.onChangeNote(code, e.target.value)}
                    onBlur={() => this.saveNote(code)}
                />
                {guardado && <small className="text-muted">Motivo guardado</small>}
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }
        const { items, summary, loading, error, responsibles } = this.state;
        const sinResponsables = responsibles.trim() === '';
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={23} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Productos" subtitle="Conteo de stock" />
                            {error && <div className="alert alert-danger">{error}</div>}
                            {summary && (
                                <div className="alert alert-info">
                                    {summary.ok} contado(s) OK · {summary.recontar} para recontar · {summary.discrepancia} discrepancia(s) (al control)
                                    {/* Desde el home NO se vuelve solo: este resumen dice qué hay que
                                        recontar, y saltar al home lo escondería. Se ofrece la vuelta. */}
                                    {this._desdeHome ? (
                                        <Link to={homeDelModo()} className="btn btn-sm btn-primary ml-3">Volver al inicio</Link>
                                    ) : null}
                                </div>
                            )}
                            <div className="card card-small mb-4">
                                <div className="card-header border-bottom">
                                    <h6 className="m-0">Lista del día — contá a ciegas y confirmá</h6>
                                </div>
                                <div className="card-body p-0 pb-3">
                                    {loading ? <p className="p-3 m-0">Cargando…</p> : (
                                        <table className="table mb-0">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="border-0">Producto</th>
                                                    <th className="border-0">Tu conteo</th>
                                                    <th className="border-0">Estado</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map((it) => {
                                                    const status = Number(it.status);
                                                    const pending = status === STATUS.PENDING;
                                                    const needsRecount = pending && Number(it.attemptNo) > 0;
                                                    return (
                                                        <tr key={it.productCode} className={needsRecount ? 'table-warning' : ''}>
                                                            <td>{it.name || it.productCode}</td>
                                                            <td style={{ maxWidth: 200 }}>
                                                                {pending ? (
                                                                    <>
                                                                        <input
                                                                            type="number"
                                                                            min="0"
                                                                            className="form-control"
                                                                            value={this.state.inputs[it.productCode] || ''}
                                                                            onChange={(e) => this.onChange(it.productCode, e.target.value)}
                                                                            onFocus={() => this.onFocusInput(it.productCode)}
                                                                            onBlur={() => this.saveDraft(it.productCode)}
                                                                        />
                                                                        {/* Sólo para el admin: al encargado el backend no le manda el dato. */}
                                                                        {it.systemStock != null && (
                                                                            <small className="text-muted">
                                                                                Según el sistema debería haber <strong>{it.systemStock}</strong>
                                                                            </small>
                                                                        )}
                                                                    </>
                                                                ) : status === STATUS.COUNTED ? (
                                                                    <span>{it.countedQuantity}</span>
                                                                ) : (
                                                                    <span>{it.countedQuantity != null ? it.countedQuantity : '—'}</span>
                                                                )}
                                                            </td>
                                                            <td style={{ maxWidth: 420 }}>
                                                                {this.renderEstado(it)}
                                                                {status === STATUS.DISCREPANCY && this.renderMotivo(it)}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="card-footer">
                                    <div className="row align-items-end">
                                        <div className="col-md-6 mb-2 mb-md-0">
                                            {/* El login del mostrador es compartido: sin este campo, "quién contó"
                                                no lo responde nadie. Por eso bloquea el confirmar. */}
                                            <label className="m-0" htmlFor="stock-count-responsibles">
                                                <strong>Responsable(s) del conteo</strong>
                                            </label>
                                            <input
                                                id="stock-count-responsibles"
                                                type="text"
                                                className={'form-control' + (sinResponsables ? ' is-invalid' : '')}
                                                maxLength={256}
                                                placeholder="Nombre y apellido (o varios, separados por coma)"
                                                value={responsibles}
                                                onChange={(e) => this.onChangeResponsibles(e.target.value)}
                                            />
                                            <small className="text-muted">
                                                Quién contó hoy. Todos entran con el mismo usuario, así que este es el único registro.
                                            </small>
                                        </div>
                                        <div className="col-md-6 text-md-right">
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={sinResponsables}
                                                onClick={this.confirmar}
                                            >
                                                Confirmar conteo
                                            </button>
                                            {sinResponsables && (
                                                <div><small className="text-danger">Poné quién contó para poder confirmar.</small></div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default StockCountPage;
